import { VRMUtils, type VRM } from '@pixiv/three-vrm';
import './ui/styles.css';

import { Stage, type Framing } from './stage/Stage';
import { loadAvatar, type AvatarLicense, type LoadedAvatar } from './stage/AvatarLoader';
import { AvatarDriver, type DriverConfig } from './driver/AvatarDriver';
import { WebcamSource } from './tracking/WebcamSource';
import { VMCSource } from './tracking/VMCSource';
import type { TrackingSource } from './tracking/TrackingSource';
import { getModel, putModel } from './storage/modelStore';
import { EMOTIONS, Panel } from './ui/panel';
import { SceneManager } from './scene/SceneManager';
import type { PoseFrame } from './types';

/** Key the current model is cached under so an OBS URL can point at it. */
const IDB_KEY = 'last';
const IDB_REF = `idb:${IDB_KEY}`;

const params = new URLSearchParams(location.search);
const obsMode = params.get('obs') === '1';
if (obsMode) document.body.classList.add('obs');

const stageRoot = must<HTMLElement>('#stage');
const uiRoot = must<HTMLElement>('#ui');

const stage = new Stage(stageRoot);
const driver = new AvatarDriver();
const config: DriverConfig = driver.config;

// The scene document: background + overlay items, one shared frame with the
// avatar. Editing is disabled in OBS mode — there it is pure display.
const sceneManager = new SceneManager(stageRoot, !obsMode);

let avatar: LoadedAvatar | null = null;
let framing: Framing = (params.get('framing') as Framing) ?? 'bust';
let source: TrackingSource | null = null;
let trackingWanted = false;

/** Dev-only synthetic frame override, so the rig can be tested without a camera. */
let debugFrame: PoseFrame | null = null;
let sourceKind: SourceKind = (params.get('source') as SourceKind) ?? 'webcam';

type SourceKind = 'webcam' | 'vmc';

/**
 * How the current avatar can be referred to in a URL. A dropped file has no
 * addressable URL of its own, so it is cached in IndexedDB and referenced as
 * `idb:last` — without this the copied OBS URL would open an empty scene.
 */
let modelRef: string | null = null;

let creditVisible = params.get('credit') !== '0';

const fileInput = document.createElement('input');
fileInput.type = 'file';
fileInput.accept = '.vrm,model/gltf-binary';
fileInput.addEventListener('change', () => {
  const file = fileInput.files?.[0];
  if (file) void openFile(file);
});

const panel = new Panel(
  uiRoot,
  config,
  {
    onPickFile: () => fileInput.click(),
    onLoadFixture: (path) => void openUrl(path),
    onToggleTracking: () => void toggleTracking(),
    onConfigChange: (patch) => {
      Object.assign(config, patch);
      driver.resetFilters();
      if ('pitchOffset' in patch || 'yawOffset' in patch || 'rollOffset' in patch) {
        saveCalibration();
      }
    },
    onFramingChange: (next) => {
      framing = next;
      if (avatar) stage.frame(avatar.vrm, framing);
    },
    onSourceChange: (kind) => {
      sourceKind = kind as SourceKind;
      // Restart so the change takes effect immediately rather than at the next
      // manual toggle.
      if (trackingWanted) {
        void (async () => {
          await toggleTracking();
          await toggleTracking();
        })();
      }
    },
    onCopyObsUrl: () => void copyObsUrl(),
    onHandConfigChange: (patch) => {
      Object.assign(driver.hands.config, patch);
      driver.hands.reset();
    },
    onResetView: () => {
      stage.resetView();
      panel.setNotice('시야를 초기화했습니다.');
    },
    onEmotion: (name) => chooseEmotion(name),
    onCopySceneLink: () => void copySceneLink(),
    onCalibrate: () => {
      if (driver.calibrate()) {
        panel.syncSliders();
        saveCalibration();
        panel.setNotice('현재 자세를 정면으로 설정했습니다.');
      } else {
        panel.setNotice('얼굴이 잡히지 않았습니다 — 트래킹을 켜고 다시 시도하세요.');
      }
    },
    onResetCalibration: () => {
      driver.resetCalibration();
      panel.syncSliders();
      saveCalibration();
      panel.setNotice('보정을 초기화했습니다.');
    },
    onCreditVisibilityChange: (visible) => {
      creditVisible = visible;
      panel.setCreditVisible(visible);
    },
  },
  driver.hands.config,
  sceneManager,
  { creditVisible, sourceKind },
);

panel.setTrackingState('off');
panel.setCreditVisible(creditVisible);

sceneManager.onSelect = (item) => panel.renderInspector(item);
sceneManager.onChange = () => {
  emotionChannel?.postMessage({ type: 'scene', encoded: sceneManager.serialize() });
};

// Scene load priority: URL hash (self-contained — survives into a real OBS
// browser source, which is a separate browser sharing no storage with Chrome),
// then localStorage for same-browser continuity.
{
  const hashMatch = /[#&]s=([A-Za-z0-9_-]+)/.exec(location.hash);
  const fromHash = hashMatch ? SceneManager.parseEncoded(hashMatch[1]!) : null;
  if (fromHash) sceneManager.load(fromHash);
  else sceneManager.loadFromStorage();
}

async function copySceneLink(): Promise<void> {
  const url = new URL(location.href);
  url.hash = sceneManager.isEmpty ? '' : `s=${sceneManager.serialize()}`;
  const text = url.toString();
  try {
    await navigator.clipboard.writeText(text);
    panel.setNotice(
      text.length > 8000
        ? '링크를 복사했지만 이미지 때문에 매우 깁니다 — 작은 이미지를 권합니다.'
        : '씬 링크를 복사했습니다. 이 씬이 누구에게나 그대로 열립니다.',
    );
  } catch {
    panel.showError('복사 실패 — 주소창의 URL을 직접 복사하세요.');
  }
}

// ---------------------------------------------------------------- avatar I/O

async function openFile(file: File): Promise<void> {
  try {
    panel.showError(null);
    const buffer = await file.arrayBuffer();

    // Cache before parsing: loadAvatar consumes the buffer via the glTF parser,
    // and a copy kept here is what makes the model reachable from OBS.
    await putModel(IDB_KEY, file.name, buffer.slice(0));
    modelRef = IDB_REF;

    await mount(await loadAvatar(buffer));
  } catch (error) {
    panel.showError(describe(error));
  }
}

async function openUrl(url: string): Promise<void> {
  try {
    panel.showError(null);

    if (url.startsWith('idb:')) {
      const stored = await getModel(url.slice(4));
      if (!stored) {
        panel.showError('저장된 모델이 없습니다 — 이 브라우저에서 VRM을 한 번 열어주세요.');
        return;
      }
      modelRef = url;
      await mount(await loadAvatar(stored.buffer));
      return;
    }

    modelRef = url;
    await mount(await loadAvatar(url));
  } catch (error) {
    panel.showError(describe(error));
  }
}

async function mount(next: LoadedAvatar): Promise<void> {
  if (avatar) {
    stage.scene.remove(avatar.vrm.scene);
    VRMUtils.deepDispose(avatar.vrm.scene);
  }

  avatar = next;
  stage.scene.add(next.vrm.scene);
  stage.frame(next.vrm, framing);
  applyViewParams();
  driver.attach(next.vrm, next.morphs, stage.scene);

  panel.setDropzoneVisible(false);
  panel.setLicense(next.license);
  panel.setCreditVisible(creditVisible);
  warnIfRestricted(next.license);

  if (!obsMode && !trackingWanted) await toggleTracking();
}

function warnIfRestricted(license: AvatarLicense): void {
  if (license.authorOnly) {
    console.info(
      '[vrm-stage] This model is marked author-only. Fine if it is yours; not redistributable.',
    );
  }
}

// ---------------------------------------------------------------- tracking

async function toggleTracking(): Promise<void> {
  if (trackingWanted) {
    trackingWanted = false;
    await source?.stop();
    source = null;
    panel.setTrackingState('off');
    return;
  }

  trackingWanted = true;
  panel.setTrackingState('starting');
  try {
    source = sourceKind === 'vmc' ? new VMCSource() : new WebcamSource();
    await source.start();
    panel.setTrackingState('on');
    panel.setNotice(source.info.detail);
    panel.showError(null);
  } catch (error) {
    trackingWanted = false;
    source = null;
    panel.setTrackingState('off');
    panel.showError(`트래킹을 시작할 수 없습니다 — ${describe(error)}`);
  }
}

// ---------------------------------------------------------------- OBS

async function copyObsUrl(): Promise<void> {
  const url = new URL(location.href);
  url.search = '';
  url.searchParams.set('obs', '1');
  url.searchParams.set('framing', framing);
  url.searchParams.set('source', sourceKind);
  if (modelRef) url.searchParams.set('model', modelRef);
  if (!creditVisible) url.searchParams.set('credit', '0');

  // Calibration is per-seat, so it has to travel with the URL or the OBS view
  // reverts to the raised-chin pose the operator just corrected.
  if (config.pitchOffset) url.searchParams.set('pitch', config.pitchOffset.toFixed(4));
  if (config.yawOffset) url.searchParams.set('yaw', config.yawOffset.toFixed(4));
  if (config.rollOffset) url.searchParams.set('roll', config.rollOffset.toFixed(4));

  // Likewise the hand-composed framing: the whole point of Ctrl-dragging the
  // avatar into place is that the stream shows what was arranged.
  const view = stage.getView();
  if (view.panX) url.searchParams.set('px', view.panX.toFixed(4));
  if (view.panY) url.searchParams.set('py', view.panY.toFixed(4));
  if (view.zoom !== 1) url.searchParams.set('zoom', view.zoom.toFixed(4));

  // The scene rides along in the hash so a real OBS browser source — a separate
  // browser with none of our storage — reproduces it from the URL alone.
  if (!sceneManager.isEmpty) url.hash = `s=${sceneManager.serialize()}`;

  const text = url.toString();
  try {
    await navigator.clipboard.writeText(text);
    panel.setNotice(
      modelRef
        ? 'URL을 복사했습니다. OBS 브라우저 소스에 붙여넣으세요.'
        : 'URL을 복사했지만 모델이 없습니다 — VRM을 먼저 여세요.',
    );
    panel.showError(null);
  } catch {
    panel.showError(`복사 실패 — 직접 사용하세요: ${text}`);
  }
}

// ---------------------------------------------------------------- emotions

/**
 * The editor tab and the OBS browser source are separate page instances, so a
 * hotkey pressed here would never reach the stream without this channel. Every
 * same-origin instance hears the change and mirrors it.
 */
const emotionChannel = 'BroadcastChannel' in window ? new BroadcastChannel('vrm-stage') : null;

if (emotionChannel) {
  emotionChannel.onmessage = (event: MessageEvent) => {
    const data = event.data as { type?: string; name?: string | null; encoded?: string };
    if (data?.type === 'emotion') {
      driver.forceEmotion(data.name ?? null);
      panel.setActiveEmotion(data.name ?? null);
    } else if (data?.type === 'scene' && typeof data.encoded === 'string') {
      // Live scene edits from the editor tab. load() does not emit, so this
      // cannot echo back and loop.
      const spec = SceneManager.parseEncoded(data.encoded);
      if (spec) sceneManager.load(spec);
    }
  };
}

function chooseEmotion(name: string | null): void {
  const resolved = driver.toggleEmotion(name);
  panel.setActiveEmotion(resolved);
  emotionChannel?.postMessage({ type: 'emotion', name: resolved });
}

window.addEventListener('keydown', (event) => {
  const target = event.target as HTMLElement | null;
  if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  // Scene selection owns Delete, and owns Escape while something is selected.
  const selected = sceneManager.selected;
  if (selected && event.key === 'Delete') {
    sceneManager.removeItem(selected.id);
    return;
  }
  if (selected && event.key === 'Escape') {
    sceneManager.select(null);
    return;
  }

  const index = '123456'.indexOf(event.key);
  if (index >= 0) {
    chooseEmotion(EMOTIONS[index]![0]);
  } else if (event.key === '0' || event.key === 'Escape') {
    chooseEmotion(null);
  }
});

// ---------------------------------------------------------------- drag & drop

window.addEventListener('dragover', (event) => {
  event.preventDefault();
  document.body.classList.add('dragging');
});
window.addEventListener('dragleave', () => document.body.classList.remove('dragging'));
window.addEventListener('drop', (event) => {
  event.preventDefault();
  document.body.classList.remove('dragging');
  const file = event.dataTransfer?.files?.[0];
  if (file) void openFile(file);
});

// ---------------------------------------------------------------- render loop

let debugAccumulator = 0;

function animate(): void {
  requestAnimationFrame(animate);

  const delta = stage.tick();
  const vrm: VRM | undefined = avatar?.vrm;

  if (vrm) {
    // Spring bones need every frame even when tracking is off, otherwise hair
    // freezes mid-swing the moment the camera stops.
    driver.update(vrm, debugFrame ?? source?.read() ?? null, delta);
  }

  stage.render();

  debugAccumulator += delta;
  if (debugAccumulator > 0.1) {
    debugAccumulator = 0;
    panel.setDebug(driver.debug);
  }
}

animate();

// Test seam: lets an automated session (or a curious person in devtools) inject
// PoseFrames and inspect the result numerically. Dev builds only.
if (import.meta.env.DEV) {
  Object.defineProperty(window, '__stage', {
    value: {
      stage,
      driver,
      get avatar() {
        return avatar;
      },
      setDebugFrame(frame: PoseFrame | null) {
        debugFrame = frame;
      },
      scene: sceneManager,
    },
  });
}

// ---------------------------------------------------------------- startup

applyCalibrationParams();

const modelParam = params.get('model');
if (modelParam) {
  void openUrl(modelParam);
} else if (obsMode) {
  // An OBS URL without a model is almost always a stale copy; fall back to
  // whatever this browser last loaded rather than rendering nothing.
  void openUrl(IDB_REF);
}

const CALIBRATION_KEY = 'vrm-stage:calibration';

/**
 * Calibration is per-seat and survives reloads: URL params win (an OBS source
 * must render exactly what was copied), otherwise the last local calibration is
 * restored so the editor does not reset to a raised chin on every refresh.
 */
function applyCalibrationParams(): void {
  const stored = readStoredCalibration();
  if (stored) Object.assign(config, stored);

  for (const [param, key] of [
    ['pitch', 'pitchOffset'],
    ['yaw', 'yawOffset'],
    ['roll', 'rollOffset'],
  ] as const) {
    const raw = params.get(param);
    if (raw === null) continue;
    const value = Number(raw);
    if (Number.isFinite(value)) config[key] = value;
  }
  panel.syncSliders();
}

/**
 * Restores a composed view from the URL. Applied after `frame()`, which resets
 * the base framing whenever a model or framing mode changes.
 */
function applyViewParams(): void {
  const px = Number(params.get('px'));
  const py = Number(params.get('py'));
  const zoom = Number(params.get('zoom'));
  stage.setView({
    panX: params.get('px') !== null && Number.isFinite(px) ? px : undefined,
    panY: params.get('py') !== null && Number.isFinite(py) ? py : undefined,
    zoom: params.get('zoom') !== null && Number.isFinite(zoom) ? zoom : undefined,
  });
}

function readStoredCalibration(): Partial<DriverConfig> | null {
  try {
    const raw = localStorage.getItem(CALIBRATION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const result: Partial<DriverConfig> = {};
    for (const key of ['pitchOffset', 'yawOffset', 'rollOffset'] as const) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'number' && Number.isFinite(value)) result[key] = value;
    }
    return result;
  } catch {
    return null;
  }
}

function saveCalibration(): void {
  try {
    localStorage.setItem(
      CALIBRATION_KEY,
      JSON.stringify({
        pitchOffset: config.pitchOffset,
        yawOffset: config.yawOffset,
        rollOffset: config.rollOffset,
      }),
    );
  } catch {
    // Private browsing or a full quota — calibration just will not persist.
  }
}

// ---------------------------------------------------------------- helpers

function must<T extends Element>(selector: string): T {
  const node = document.querySelector<T>(selector);
  if (!node) throw new Error(`Missing required element: ${selector}`);
  return node;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
